// Meldungen des Planungsassistenten — Ersatz für MatSnackBar.
//
// Angular Material ist für Neuentwicklung nicht mehr vorgesehen (siehe AGENTS.md), und die
// einfache Snackbar konnte ohnehin nur einen einzigen Aktionsknopf darstellen. Für die
// Fehlermeldung der Tourenberechnung wird aber ein zweiter Knopf gebraucht, der die
// Debug-Aufzeichnung öffnet.
//
// Verhalten bewusst wie bisher: Es ist immer nur EINE Meldung sichtbar, eine neue ersetzt die
// vorherige. Das verhindert, dass beim Laden vieler Aufträge dutzende Meldungen übereinander
// stapeln.
//
// Die Datei importiert bewusst keine PA-Klasse und kein Singleton, ist also von überall
// zyklenfrei benutzbar.

import { Subject } from "rxjs";

export type PANotificationType = 'info' | 'success' | 'error'

export interface PANotificationAction {
  label: string
  handler: () => void
  /** true = Meldung bleibt nach dem Klick stehen (z. B. „Debug-Infos" neben „ok") */
  keep_open?: boolean
}

export interface PANotificationConfig {
  type?: PANotificationType
  position?: 'top' | 'bottom'
  /** Zusätzliche Knöpfe neben der Standard-Aktion */
  extra_actions?: PANotificationAction[]
}

export interface PANotification {
  id: number
  message: string
  type: PANotificationType
  position: 'top' | 'bottom'
  actions: PANotificationAction[]
}

/** Rückgabe von open(); erlaubt das automatische Ausblenden wie das frühere _dismissAfter */
export class PANotificationRef {
  private timeout: ReturnType<typeof setTimeout> | null = null

  constructor(private readonly notification: PANotification) {}

  /** Blendet die Meldung nach der angegebenen Zeit aus, sofern sie dann noch sichtbar ist */
  dismissAfter(milliseconds: number): PANotificationRef {
    this.timeout = setTimeout(() => PANotifications.dismiss(this.notification.id), milliseconds)
    return this
  }

  dismiss(): void {
    if (this.timeout) clearTimeout(this.timeout)
    PANotifications.dismiss(this.notification.id)
  }
}

export class PANotifications {

  private static _current: PANotification | null = null
  private static nextId = 1

  /** Feuert bei jeder Änderung der sichtbaren Meldung */
  static readonly changes = new Subject<PANotification | null>()

  static get current(): PANotification | null {
    return this._current
  }

  /**
   * Zeigt eine Meldung an und ersetzt dabei eine bereits sichtbare.
   * @param action Beschriftung des Schließen-Knopfes; null blendet ihn aus
   */
  static open(message: string, action: string | null = 'Ok', config?: PANotificationConfig): PANotificationRef {
    const notification: PANotification = {
      id: this.nextId++,
      message: message,
      type: config?.type || 'info',
      position: config?.position || 'bottom',
      actions: []
    }

    for (const extra_action of config?.extra_actions || []) {
      notification.actions.push(extra_action)
    }
    if (action) {
      notification.actions.push({ label: action, handler: () => PANotifications.dismiss(notification.id) })
    }

    this._current = notification
    this.changes.next(notification)
    return new PANotificationRef(notification)
  }

  /** Schließt die Meldung, sofern sie nicht bereits durch eine neuere ersetzt wurde */
  static dismiss(id: number): void {
    if (!this._current || this._current.id != id) return
    this._current = null
    this.changes.next(null)
  }

  static clear(): void {
    this._current = null
    this.changes.next(null)
  }
}
